/**
 * Console agent — page de login dédiée /agent.
 *
 * UI minimaliste (centrée, pas de marketing column) avec branding
 * « Console Agent ». Le form dispatch le même `login()` que /login standard.
 * Une fois le token posé en Redux, App.jsx bascule vers UserRoutes (ou
 * AdminRoutes si SuperAdmin) qui re-route /agent vers AgentGate. AgentGate
 * fait alors le check de role et redirige vers /member/time2chat/conversations/all
 * si l'user est agent, sinon affiche un message d'erreur.
 *
 * Pourquoi 2 composants (AgentLogin + AgentGate) : App.jsx choisit entre
 * GeneralRoutes (sans token) et UserRoutes/AdminRoutes (avec token). On a
 * donc besoin d'une route /agent dans chacun.
 */
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Headset, Eye, EyeOff, Loader2 } from 'lucide-react';

import { login } from '@/store/actions/creators/authActions';
import { errorMessageToast } from '@/utils/common/helper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useTheme } from '@/components/ThemeProvider';
import LogoLight from '@/assets/images/logo_smsreflex_light.png';
import LogoDark from '@/assets/images/logo_smsreflex_dark.png';

const schema = z.object({
  username: z.string().min(1, 'login required'),
  password: z.string().min(1, 'password required'),
  keepSignedIn: z.boolean().default(false),
});

type FormValues = z.infer<typeof schema>;

export default function AgentLogin() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  useTheme(); // ensure ThemeProvider is mounted
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: '', password: '', keepSignedIn: false },
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      await dispatch(
        // @ts-expect-error legacy redux-thunk
        login(values.username.trim(), values.password.trim(), values.keepSignedIn),
      );
      // Au succès, App.jsx re-render avec UserRoutes/AdminRoutes → /agent
      // sera matched par AgentGate qui fait le routing final.
    } catch (e: any) {
      errorMessageToast(
        e?.response?.data?.message ??
          t('AGENT.LOGIN_FAILED') ??
          'Login failed. Check your credentials.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <div className="w-full max-w-md rounded-2xl border bg-card p-6 shadow-md md:p-8">
        <div className="mb-6 flex flex-col items-center gap-3">
          <img src={LogoLight} alt="SMSReflex" className="h-10 w-auto dark:hidden" />
          <img src={LogoDark} alt="SMSReflex" className="hidden h-10 w-auto dark:block" />
          <div className="flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium uppercase tracking-wide text-primary">
            <Headset className="h-3.5 w-3.5" />
            {t('AGENT.CONSOLE_BADGE') ?? 'Console Agent'}
          </div>
          <h1 className="text-center text-xl font-semibold">
            {t('AGENT.LOGIN_TITLE') ?? 'Se connecter à la console agent'}
          </h1>
          <p className="text-center text-sm text-muted-foreground">
            {t('AGENT.LOGIN_HINT') ??
              'Espace dédié aux agents Time2Chat pour répondre aux conversations clients.'}
          </p>
        </div>

        <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="ag-username">
              {t('LOGIN.USERNAME') ?? 'Identifiant'}
            </Label>
            <Input
              id="ag-username"
              autoComplete="username"
              autoFocus
              {...register('username')}
              aria-invalid={Boolean(errors.username)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ag-password">
              {t('LOGIN.PASSWORD') ?? 'Mot de passe'}
            </Label>
            <div className="relative">
              <Input
                id="ag-password"
                type={showPwd ? 'text' : 'password'}
                autoComplete="current-password"
                {...register('password')}
                aria-invalid={Boolean(errors.password)}
                className="pr-9"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                aria-label={showPwd ? 'Hide password' : 'Show password'}
              >
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <Checkbox
                id="ag-remember"
                checked={form.watch('keepSignedIn')}
                onCheckedChange={(c) =>
                  form.setValue('keepSignedIn', c === true)
                }
              />
              <span>{t('LOGIN.REMEMBER_ME') ?? 'Se souvenir de moi'}</span>
            </label>
            <Link
              to="/forgotpassword"
              className="text-primary underline-offset-4 hover:underline"
            >
              {t('LOGIN.FORGOT_PASSWORD') ?? 'Mot de passe oublié ?'}
            </Link>
          </div>

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('COMMON.LOADING') ?? 'Chargement...'}
              </>
            ) : (
              t('AGENT.LOGIN_BUTTON') ?? 'Entrer dans la console'
            )}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            {t('AGENT.STANDARD_PORTAL_HINT') ??
              'Vous n’êtes pas un agent ?'}{' '}
            <Link to="/login" className="text-primary underline-offset-4 hover:underline">
              {t('AGENT.STANDARD_PORTAL_LINK') ?? 'Portail standard'}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
